<template>
  <div>
    <!-- Push Permission Banner -->
    <div class="bg-light radius-small pd-small mn-b-small">
      <div class="flex flex-v-center flex-between">
        <div class="w-100 flex flex-v-center gap-small">
          <div class="h4">🔔</div>
          <div class="flex-column flex gap-micro">
            <p class="fw-medium">{{ t('notifications.preferences.pushTitle') }}</p>
            <p class="p-small" :class="pushStatusClass">{{ pushStatusText }}</p>
          </div>
        </div>
        <Checkbox
          mode="switch"
          :radio="pushEnabled"
          :disabled="!pushSupported"
          @update:radio="togglePush"
          class="flex-shrink-0"
        />
      </div>
    </div>

    <!-- Channel preferences — always visible -->
    <p class="opacity-50 mn-b-small">{{ t('notifications.preferences.chooseHow') }}</p>

    <div v-if="loading" class="pd-large t-center opacity-50">
      <Loader />
    </div>

    <div v-else class="flex-column flex gap-small">
      <!-- Standard channels -->
      <div class="gap-thin flex-column flex">
        <div
          v-for="channelType in standardChannels"
          :key="channelType"
          class="flex flex-v-center flex-between bg-light radius-small pd-small"
        >
          <div class="flex w-100 flex-v-center gap-small mn-r-small">
            <div class="h4">{{ getChannelIcon(channelType) }}</div>
            <div class="flex-column flex gap-micro">
              <p class="fw-medium">{{ getChannelName(channelType) }}</p>
              <p class="p-small opacity-50">{{ getChannelDescription(channelType) }}</p>
            </div>
          </div>
          <Checkbox
            mode="switch"
            :radio="preferences[channelType]"
            @update:radio="updatePreference(channelType, $event)"
            class="flex-shrink-0"
          />
        </div>
      </div>

      <!-- Messengers -->
      <div>
        <p class="fw-medium opacity-50 mn-b-thin">{{ t('notifications.preferences.messengers') }}</p>
        <div class="gap-thin flex-column flex">
          <!-- Telegram -->
          <div class="flex flex-v-center flex-between bg-light radius-small pd-small">
            <div class="flex w-100 flex-v-center gap-small mn-r-small">
              <div class="h4">📬</div>
              <div class="flex-column flex gap-micro">
                <p class="fw-medium">{{ t('notifications.preferences.telegramTitle') }}</p>
                <p class="p-small opacity-50">{{ t('notifications.preferences.telegramDescription') }}</p>
              </div>
            </div>
            <div class="flex flex-v-center gap-small flex-shrink-0">
              <template v-if="telegramConnected">
                <Button
                  :submit="handleDisconnectTelegram"
                  class="bg-white t-dark pd-thin pd-h-small p-small"
                >{{ t('notifications.preferences.disconnect') }}</Button>
              </template>
              <template v-else>
                <Button
                  :submit="handleConnectTelegram"
                  :loading="telegramLinking"
                  class="bg-main t-white pd-thin pd-h-small p-small"
                >{{ t('notifications.preferences.connect') }}</Button>
              </template>
            </div>
          </div>

          <!-- WhatsApp -->
          <div class="flex flex-v-center flex-between bg-light radius-small pd-small opacity-50">
            <div class="flex w-100 flex-v-center gap-small mn-r-small">
              <div class="h4">💬</div>
              <div class="flex-column flex gap-micro">
                <p class="fw-medium">{{ t('notifications.preferences.whatsappTitle') }}</p>
                <p class="p-small opacity-50">{{ t('notifications.preferences.whatsappComingSoon') }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Save / Cancel -->
      <div class="flex flex-end gap-small mn-t-thin">
        <Button
          v-if="hasChanges"
          :submit="resetChanges"
          :showLoader="false"
          :showSucces="false"
          class="bg-white t-dark"
        >
          {{ t('notifications.preferences.cancel') }}
        </Button>
        <Button
          :submit="savePreferences"
          :validation="!hasChanges || saving"
          class="bg-main t-white w-100"
        >
          {{ saving ? t('notifications.preferences.saving') : t('notifications.preferences.save') }}
        </Button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted, inject, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import Checkbox from '@martyrs/src/design-system/components/Checkbox/Checkbox.vue';
import Button from '@martyrs/src/design-system/components/Button/Button.vue';
import Loader from '@martyrs/src/design-system/components/Loader/Loader.vue';

const { t } = useI18n();

// Get notification preferences functionality
const {
  preferences: storePreferences,
  loading,
  getPreferences,
  updatePreferences,
  registerWebPush,
  unregisterWebPush,
  getSubscriptionState,
  telegramConnected: storeTelegramConnected,
  generateTelegramLink,
  getTelegramStatus,
  disconnectTelegram,
} = inject('useNotifications')();

// Local reactive state for form
const preferences = reactive({...storePreferences.value});
const originalPreferences = ref({});
const saving = ref(false);

// Push permission state
const pushSupported = ref(typeof window !== 'undefined' && 'serviceWorker' in navigator && 'PushManager' in window);
const pushPermission = ref('default');
const pushEnabled = ref(false);

// Telegram state
const telegramConnected = ref(false);
const telegramLinking = ref(false);

// Watch for WS-driven telegram connection updates
watch(storeTelegramConnected, (val) => {
  if (val) telegramConnected.value = true;
});

const pushStatusText = computed(() => {
  if (!pushSupported.value) return t('notifications.preferences.pushNotSupported');
  if (pushEnabled.value) return t('notifications.preferences.pushEnabled');
  // НЕ показываем "Blocked" только по permission === 'denied' — на iOS это ненадёжно
  // Показываем "Blocked" только если permission denied И подписки нет
  if (pushPermission.value === 'denied' && !pushEnabled.value) return t('notifications.preferences.pushBlocked');
  return t('notifications.preferences.pushAllow');
});

const pushStatusClass = computed(() => {
  if (!pushSupported.value) return 'opacity-50';
  if (pushEnabled.value) return 't-green';
  if (pushPermission.value === 'denied') return 't-red';
  return 'opacity-50';
});

const togglePush = async (value) => {
  // Переключаем свитч сразу — optimistic UI
  pushEnabled.value = value;

  if (value) {
    try {
      // 1. Запрашиваем permission в контексте user gesture (ОБЯЗАТЕЛЬНО для Safari)
      //    НО результат НЕ проверяем — на iOS он может врать "denied"
      if ('Notification' in window) {
        await Notification.requestPermission();
      }
      // 2. Пробуем subscribe — если реально нет разрешения, выкинет NotAllowedError
      //    Если iOS вернул "denied" но реально granted — subscribe сработает
      await registerWebPush();
      // 3. Перечитываем реальный state через надёжные API
      const { permission } = await getSubscriptionState();
      pushPermission.value = permission;
    } catch (error) {
      console.error('Push subscribe error:', error);
      // Revert — подписка не удалась
      pushEnabled.value = false;
      const { permission } = await getSubscriptionState();
      pushPermission.value = permission;
    }
  } else {
    unregisterWebPush().catch(err => console.error('Push unsubscribe error:', err));
  }
};

// Standard channels (exclude messengers — they have their own section)
const messengerChannels = ['telegram', 'whatsapp'];
const standardChannels = computed(() =>
  Object.keys(preferences).filter(ch => !messengerChannels.includes(ch))
);

// Computed properties
const hasChanges = computed(() => {
  return Object.keys(preferences).some(key =>
    preferences[key] !== originalPreferences.value[key]
  );
});

// Methods
const updatePreference = (channelType, value) => {
  preferences[channelType] = value;
};

const savePreferences = async () => {
  try {
    saving.value = true;
    await updatePreferences(preferences);
    originalPreferences.value = {...preferences};
    saving.value = false;
  } catch (error) {
    console.error('Error saving preferences:', error);
    saving.value = false;
  }
};

const resetChanges = () => {
  Object.keys(preferences).forEach(key => {
    preferences[key] = originalPreferences.value[key];
  });
};

const handleConnectTelegram = async () => {
  try {
    telegramLinking.value = true;
    
    // Открываем окно синхронно, пока ещё в контексте user gesture
    const win = window.open('', '_blank');
    
    const result = await generateTelegramLink();
    
    if (result?.deepLink) {
      if (win) {
        win.location.href = result.deepLink;
      } else {
        // Fallback если всё равно заблокировали
        window.location.href = result.deepLink;
      }
    } else {
      win?.close();
    }
    
    telegramLinking.value = false;
  } catch (error) {
    console.error('Error generating Telegram link:', error);
    telegramLinking.value = false;
  }
};

const handleDisconnectTelegram = async () => {
  await disconnectTelegram();
  telegramConnected.value = false;
};

const getChannelIcon = (channelType) => {
  switch (channelType) {
    case 'web': return '🖥️';
    case 'push': return '📱';
    case 'email': return '📧';
    case 'sms': return '📱';
    case 'telegram': return '📬';
    case 'whatsapp': return '💬';
    default: return '🔔';
  }
};

const getChannelName = (channelType) => {
  const key = `notifications.preferences.channels.${channelType}.name`;
  const translated = t(key);
  // Если ключа нет — t() возвращает сам ключ, тогда фоллбек на channelType.
  return translated === key ? channelType : translated;
};

const getChannelDescription = (channelType) => {
  const key = `notifications.preferences.channels.${channelType}.description`;
  const translated = t(key);
  return translated === key ? '' : translated;
};

// Lifecycle
onMounted(async () => {
  // Определяем реальное состояние подписки через getSubscriptionState (robust для iOS)
  if (pushSupported.value) {
    try {
      const { permission, subscribed } = await getSubscriptionState();
      pushPermission.value = permission;
      pushEnabled.value = subscribed;
    } catch (e) {
      console.error('Failed to get subscription state:', e);
    }
  }

  await getPreferences();

  // Check Telegram connection status
  try {
    const tgStatus = await getTelegramStatus();
    if (tgStatus) {
      telegramConnected.value = tgStatus.connected;
    }
  } catch (e) {
    console.error('Failed to get Telegram status:', e);
  }

  // Copy store preferences to local state
  Object.keys(storePreferences.value).forEach(key => {
    preferences[key] = storePreferences.value[key];
  });

  // Create a deep copy of the original preferences
  originalPreferences.value = {...preferences};
});
</script>
